import { useCallback, useEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { compactPrimaryButton } from "../../../components/ui/buttonStyles.js";
import { AssetCommandCard } from "./AssetCommandCard.jsx";
import { CommandViewModal } from "./CommandViewModal.jsx";
import { CommandTypeSelectModal } from "./CommandTypeSelectModal.jsx";
import { CreateCanCommandModal } from "./CreateCanCommandModal.jsx";
import { CommandBuilderPlaceholderModal } from "./CommandBuilderPlaceholderModal.jsx";

export function AssetCommandsSection({
  assetId,
  commands,
  loading,
  saving,
  addCommand,
  removeCommand,
}) {
  const { c, accentHex: A } = useThemeSlice();
  const [typeSelectOpen, setTypeSelectOpen] = useState(false);
  const [builderType, setBuilderType] = useState(null);
  const [viewingCommand, setViewingCommand] = useState(null);

  useEffect(() => {
    setViewingCommand(null);
  }, [assetId]);

  const openCreate = useCallback(() => {
    setBuilderType(null);
    setTypeSelectOpen(true);
  }, []);

  const closeDetail = useCallback(() => {
    setViewingCommand(null);
  }, []);

  const closeTypeSelect = useCallback(() => {
    setTypeSelectOpen(false);
  }, []);

  const closeBuilder = useCallback(() => {
    setBuilderType(null);
  }, []);

  const handleTypeSelect = useCallback((typeId) => {
    setTypeSelectOpen(false);
    setBuilderType(typeId);
  }, []);

  const handleSave = useCallback(async (payload) => {
    await addCommand({ ...payload, assetId });
  }, [assetId, addCommand]);

  const handleCardClick = useCallback((command) => {
    setViewingCommand(command);
  }, []);

  const isPlaceholderBuilder = builderType === "device-pin" || builderType === "custom";
  const sortedCommands = useMemo(
    () => [...commands].sort((a, b) => String(b.dateTime).localeCompare(String(a.dateTime))),
    [commands],
  );

  return (
    <>
      <section style={{ marginTop: 20, paddingTop: 18, borderTop: `1px solid ${c.divider}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: A }}>
              Commands
            </div>
            {!loading && (
              <div style={{ fontSize: 12, color: c.sub, marginTop: 4 }}>
                {sortedCommands.length} command{sortedCommands.length === 1 ? "" : "s"}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={openCreate}
            disabled={loading || saving}
            style={{
              ...compactPrimaryButton(A),
              opacity: loading || saving ? 0.7 : 1,
              cursor: loading || saving ? "wait" : "pointer",
              flexShrink: 0,
            }}
          >
            {Ico.plus}
            Add command
          </button>
        </div>

        {sortedCommands.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 8,
            }}
          >
            {sortedCommands.map((command) => (
              <AssetCommandCard
                key={command.id}
                command={command}
                onClick={handleCardClick}
              />
            ))}
          </div>
        )}
      </section>

      <CommandViewModal
        open={Boolean(viewingCommand)}
        command={viewingCommand}
        onClose={closeDetail}
        onDelete={removeCommand}
      />

      <CommandTypeSelectModal open={typeSelectOpen} onClose={closeTypeSelect} onSelect={handleTypeSelect} />

      <CreateCanCommandModal
        open={builderType === "can"}
        onClose={closeBuilder}
        onSave={handleSave}
        saving={saving}
      />

      <CommandBuilderPlaceholderModal
        open={isPlaceholderBuilder}
        onClose={closeBuilder}
        commandType={builderType}
      />
    </>
  );
}
